import { computed, Directive, input } from '@angular/core';
import { resolvePersonName } from '../functions/person.function';
import { AvatarPerson } from '../models/avatar-person.model';
import { GetInitialsPipe } from '../pipes/get-initials.pipe';

/**
 * Shows a person on a `dm-avatar`: their initials and their name as alt text, and, for a Neosys
 * employee, the Neosys mark as the company badge with «Neosys» as its alt text and tooltip, plus a
 * 2px light brand-red border that still shows at sizes where the badge is a dot.
 *
 * Bind none of these on the same `dm-avatar`; the directive owns them.
 */
@Directive({
  selector: 'dm-avatar[lpAvatarFromPerson]',
  host: {
    '[attr.initials]': 'initials()',
    '[attr.alt]': 'name() || null',
    '[attr.company]': 'isNeosys() ? badgeSource : null',
    '[attr.company-alt]': 'isNeosys() ? neosysLabel : null',
    '[attr.tooltip-label]': 'isNeosys() ? neosysLabel : null',
    '[style.--dm-avatar-border-color]': "isNeosys() ? 'var(--dm-color-brand-200)' : null",
    '[style.--dm-avatar-border-width]': "isNeosys() ? '2px' : null",
  },
})
export class AvatarFromPersonDirective {
  public readonly lpAvatarFromPerson = input.required<AvatarPerson | null | undefined>();

  protected readonly name = computed<string>(() => resolvePersonName(this.lpAvatarFromPerson()));
  protected readonly initials = computed<string | null>(() => {
    const person = this.lpAvatarFromPerson();
    return person ? this.getInitialsPipe.transform(person) : null;
  });
  protected readonly isNeosys = computed<boolean>(() => this.lpAvatarFromPerson()?.isNeosys ?? false);

  protected readonly badgeSource = 'assets/images/neosys-badge.svg';
  protected readonly neosysLabel = $localize`:@@common.neosys:Neosys`;

  private readonly getInitialsPipe = new GetInitialsPipe();
}
